import { Button, Popover } from '@blueprintjs/core';
import AdvancedDataTable from 'Components/Generic/AdvancedDataTable';
import DataTableHeaderTextFieldFilter from 'Components/Generic/AdvancedDataTable/DataTableHeaderTextFieldFilter';
import {
  NUMBER_LIKE_FILTER_MAP,
  TEXT_LIKE_FILTER_MAP,
} from 'Components/Generic/AdvancedDataTable/Helper';
import OrganizationMultiselect from 'Components/Generic/BOSMultiselect/OrganizationMultiselect';
import UserMultiselect from 'Components/Generic/BOSMultiselect/UserMultiselect';
import VendorMultiselect from 'Components/Generic/BOSMultiselect/VendorMultiselect';
import {
  showSaveSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import ShippingSpeedLabel from 'Components/Generic/Shipping/ShippingSpeedLabel';
import OrgNameWithDrawerButton from 'Components/SiteDrawerContainer/OrgNameWithDrawerButton';
import UserNameWithDrawerButton from 'Components/SiteDrawerContainer/UserNameWithDrawerButton';
import VendorNameWithDrawerButton from 'Components/SiteDrawerContainer/VendorNameWithDrawerButton';
import { SITE_PAGES } from 'Config';
import AppData from 'Data/AppData';
import { useAlertContext } from 'Data/context/Alerts';
import { useGlobalState } from 'Data/context/GlobalState';
import { getOrgNameFromId, getUserOrgIds } from 'Data/context/Organizations';
import { getUserNameFromId } from 'Data/context/Users';
import { getVendorNameFromId } from 'Data/context/Vendors';
import { user_org_roles, user_roles } from 'Data/Enum';
import RequestData from 'Data/RequestData';
import DataManager from 'Lib/DataManager';
import ConditionalWrapper from 'Lib/hoc/ConditionalWrapper';
import { usePersistentFiltering } from 'Lib/hooks/usePersistentFiltering';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { canUserViewAllCores, isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';
import { getStatusObject, REQUEST_STATUS, REQUEST_STATUS_KEYS } from './Enum';
import styles from './OrderTable.module.css';
import { RequestStatusLabel } from './RequestDetails/RequestStatus';
import RequestWorkflowPanel from './RequestDetails/RequestWorkflowPanel';

let DM = new DataManager();

let filterOptions = {
  id: {
    seqRestKey: '*id',
    mapToSeqRestValue: NUMBER_LIKE_FILTER_MAP,
  },
  organization: {
    seqRestKey: '*parent_organization_id',
  },
  vendor: {
    seqRestKey: '*vendor_id',
  },
  processing_user_id: {
    seqRestKey: '*processing_user_id',
  },
  requesting_user_id: {
    seqRestKey: '*requesting_user_id',
  },
  notes: {
    seqRestKey: '*notes',
    mapToSeqRestValue: TEXT_LIKE_FILTER_MAP,
  },
};

const OrderTable = ({
  title,
  getRequestListFunction,
  getExportRequestListFunction,
  persistentTableNamingPrefix = 'ORDERS',
  includeProcessorFilter = true,
  includeDeleteColumn = true,
  includeStatusColumn = false,
  hideRequesterFilter = false,
  forwardOnClaim = false,
  autoRefreshInterval = 1 * 60 * 1000, //Refresh every minute
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openDeleteDialog, showDeletedToast } = useAlertContext();
  const [data, setData] = useState();
  const [globalState] = useGlobalState();
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshTable = () => setRefreshKey((key) => key + 1);

  const [persistentFilter, updateFilterField] = usePersistentFiltering(
    `${persistentTableNamingPrefix}_FILTER`,
  );

  useEffect(() => {
    DM.add(RequestData.registerListener('LIST_LOADED', setData), RequestData);
    DM.add(
      RequestData.registerListener('LIST_LOAD_ERROR', onRequestListLoadError),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('REQUEST_CLAIMED', onRequestClaimed),
      RequestData,
    );
    DM.add(
      RequestData.registerListener(
        'REQUEST_ALREADY_CLAIMED',
        onRequestAlreadyClaimed,
      ),
      RequestData,
    );
    DM.add(
      RequestData.registerListener(
        'REQUEST_NOT_YET_SUBMITTED',
        onRequestNotYetSubmitted,
      ),
      RequestData,
    );
    DM.add(
      RequestData.registerListener('RECORDS_DELETED', onRequestDeleted),
      RequestData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    refreshTable();
  }, [globalState?.fiscalYear]);

  const onRequestListLoadError = () => {
    setData(undefined);
  };

  const claimOrder = (order) => {
    RequestData.claim(order);
  };

  const onRequestClaimed = (payload) => {
    if (payload) {
      showSaveSuccessToast('Order claimed by you.');
      forwardOnClaim && navigate(`${SITE_PAGES.ORDER_DETAIL.to}/${payload.id}`);
      !forwardOnClaim && refreshTable();
    }
  };

  const onRequestAlreadyClaimed = () => {
    showToast(
      'This request has already been claimed.  In order to change the processor, you must do so from the Request Details page.',
      'danger',
      'warning-sign',
      5000,
    );
  };
  const onRequestNotYetSubmitted = () => {
    showToast(
      'This request has not been submitted yet.',
      'danger',
      'warning-sign',
      5000,
    );
  };

  const deleteOrder = (order) => {
    RequestData.deleteRecords([order.id]);
  };
  const deleteClicked = (order) => {
    openDeleteDialog({
      objectType: 'Order',
      onConfirmDelete: () => deleteOrder(order),
    });
  };

  const onRequestDeleted = () => {
    showDeletedToast('Order');
    refreshTable();
  };

  const headers = [
    {
      label: 'ID',
      value: 'id',
      width: 80,
      sortable: true,
      filterable: true,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'ID'}
            defaultValue={persistentFilter?.id}
            onFilterValueUpdate={(e) => {
              updateFilterField('id', e.target.value);
            }}
          />
        );
      },
      render(order) {
        let orderUrl = `${location?.hash}/${order.id}`;
        return <Link to={`${orderUrl.substring(1)}`}>{order.id}</Link>;
      },
    },

    ...(includeStatusColumn
      ? [
          {
            label: 'Status',
            value: 'status',
            width: 160,
            wrapText: true,
            centeredHeader: true,
            sortable: true,
            render(order) {
              return (
                order?.status && (
                  <ConditionalWrapper
                    condition={order.status !== REQUEST_STATUS_KEYS.NEW}
                    wrapper={(children) => (
                      <Popover
                        className={styles.full_width}
                        interactionKind={'hover'}
                        content={
                          <RequestWorkflowPanel
                            title={`Order ${order.id} Workflow`}
                            request={order}
                          />
                        }>
                        {children}
                      </Popover>
                    )}>
                    <RequestStatusLabel
                      small
                      disabled
                      selectedStatus={getStatusObject(order.status)}
                    />
                  </ConditionalWrapper>
                )
              );
            },
            getText: (order) => {
              let statusObject = REQUEST_STATUS[order.status];
              return statusObject?.label || order.status;
            },
          },
        ]
      : []),

    {
      label: 'Requested',
      value: 'date_requested',
      width: 112,
      sortable: true,
      format: (date) =>
        formatDate(date) || (
          <span style={{ fontStyle: 'italic' }}>{'Pending'}</span>
        ),
      getText: (order) => formatDate(order.date_requested) || 'Pending',
    },

    {
      label: 'Core Facility',
      value: 'organization',
      fieldName: 'parent_organization_id',
      width: 144,
      sortable: true,
      sortFieldName: 'organizations.name',
      filterable: true,
      renderHeader() {
        return (
          <OrganizationMultiselect
            onSelectedOrganizationsUpdate={(orgList) =>
              updateFilterField('organization', orgList)
            }
            defaultSelectedItems={persistentFilter?.organization}
            isHeader
          />
        );
      },
      format: (orgId) => {
        return <OrgNameWithDrawerButton orgId={orgId} />;
      },
      getText: (order) => {
        return getOrgNameFromId(order.parent_organization_id);
      },
    },

    {
      label: 'Requester',
      value: 'requesting_user_id',
      width: 140,
      filterable: !hideRequesterFilter,
      renderHeader() {
        return hideRequesterFilter ? (
          'Requester'
        ) : (
          <UserMultiselect
            labelText='Requester'
            onSelectedUsersUpdate={(userList) =>
              updateFilterField('requesting_user_id', userList)
            }
            defaultSelectedItems={persistentFilter?.requesting_user_id}
            orgIds={!canUserViewAllCores() && getUserOrgIds()}
            isHeader
          />
        );
      },
      format: (requesting_user_id) => {
        return <UserNameWithDrawerButton userId={requesting_user_id} />;
      },
      getText: (order) => {
        return getUserNameFromId(order.requesting_user_id);
      },
    },

    {
      label: 'Processor',
      value: 'processing_user_id',
      width: 140,
      filterable: includeProcessorFilter,
      renderHeader() {
        return includeProcessorFilter ? (
          <UserMultiselect
            labelText='Processor'
            onSelectedUsersUpdate={(userList) =>
              updateFilterField('processing_user_id', userList)
            }
            defaultSelectedItems={persistentFilter?.processing_user_id}
            userRoles={[user_roles.BUYER.key]}
            isHeader
          />
        ) : (
          <span>Processor</span>
        );
      },
      render(order) {
        if (order.processor) {
          return <UserNameWithDrawerButton userId={order.processing_user_id} />;
        }
        return isUserAuthorized({
          roles: [user_roles.BUYER, user_roles.TRANSACTOR],
        }) && order.status > REQUEST_STATUS_KEYS.SUBMITTED ? (
          <Button
            intent='danger'
            rightIcon='confirm'
            onClick={() => claimOrder(order)}>
            Claim
          </Button>
        ) : (
          ''
        );
      },
      getText: (order) => {
        return getUserNameFromId(order.requesting_user_id);
      },
    },

    {
      label: 'Vendor',
      value: 'vendor',
      fieldName: 'vendor_id',
      width: 160,
      sortable: true,
      sortFieldName: 'vendors.name',
      filterable: true,
      renderHeader() {
        return (
          <VendorMultiselect
            onSelectedVendorsUpdate={(vendorList) =>
              updateFilterField('vendor', vendorList)
            }
            defaultSelectedItems={persistentFilter?.vendor}
            isHeader
          />
        );
      },
      format: (vendor_id) => {
        return <VendorNameWithDrawerButton vendorId={vendor_id} />;
      },
      getText: (order) => {
        return getVendorNameFromId(order.vendor_id);
      },
    },

    {
      label: 'Description',
      value: 'notes',
      filterable: true,
      truncated: true,
      minWidth: 180,
      renderHeader() {
        return (
          <DataTableHeaderTextFieldFilter
            fieldName={'Description'}
            defaultValue={persistentFilter?.notes}
            onFilterValueUpdate={(e) => {
              updateFilterField('notes', e.target.value);
            }}
          />
        );
      },
    },

    {
      label: 'Amount',
      value: 'total_amount',
      width: 90,
      moneyColumn: true,
    },

    ...(isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
      ],
    })
      ? [
          {
            label: 'Shipping',
            value: 'shipping_speed',
            width: 98,
            sortable: true,
            centeredHeader: true,
            format: (shipping_speed_key) => {
              return (
                <ShippingSpeedLabel shippingSpeedKey={shipping_speed_key} />
              );
            },
          },
        ]
      : []),

    ...(includeDeleteColumn
      ? [
          {
            label: '',
            value: '',
            isIconCol: true,
            render(order) {
              return (
                ((order?.status === REQUEST_STATUS_KEYS.NEW &&
                  (order.requesting_user_id === AppData.userID() ||
                    isUserAuthorized({
                      roles: [
                        user_roles.ADMINISTRATOR,
                        [order.parent_organization_id, user_org_roles.MANAGER],
                      ],
                    }))) ||
                  (order?.status >= REQUEST_STATUS_KEYS.SUBMITTED &&
                    ((!order?.processing_user_id &&
                      isUserAuthorized({
                        roles: [user_roles.ADMINISTRATOR],
                      })) ||
                      order?.processing_user_id === AppData.userID()))) && (
                  <DeleteButton
                    miniVersion
                    onDeleteItem={() => {
                      deleteClicked(order);
                    }}
                    tooltipText={'Delete Order'}
                  />
                )
              );
            },
          },
        ]
      : []),
  ];

  return (
    <AdvancedDataTable
      headers={headers}
      refreshKey={refreshKey}
      title={title}
      dataKey={persistentTableNamingPrefix}
      data={data}
      dataQueryFunc={getRequestListFunction}
      filterOptions={filterOptions}
      filterBy={persistentFilter}
      updateFilterField={updateFilterField}
      exportDataFunc={getExportRequestListFunction}
      autoRefreshInterval={autoRefreshInterval}
      rowHeight={50}
    />
  );
};

OrderTable.propTypes = {
  title: PropTypes.object,
  getRequestListFunction: PropTypes.func.isRequired,
  getExportRequestListFunction: PropTypes.func.isRequired,
  persistentTableNamingPrefix: PropTypes.string,
  includeProcessorFilter: PropTypes.bool,
  includeDeleteColumn: PropTypes.bool,
  includeStatusColumn: PropTypes.bool,
  hideRequesterFilter: PropTypes.bool,
  forwardOnClaim: PropTypes.bool,
  autoRefreshInterval: PropTypes.number,
};

export default OrderTable;
